import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Output,
  signal,
  DestroyRef,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { BrnInputOtp } from "@spartan-ng/brain/input-otp";
import {
  HlmButton,
  HlmLabel,
  HlmInputOtp,
  HlmInputOtpGroup,
  HlmInputOtpSlot,
  HlmSpinnerImports,
} from "@o2-fe/ui";
import { AuthenticateStateEnum } from "../../enums/authenticate.enum";
import { AuthService } from "../../services/auth.service";

@Component({
  selector: "app-confirm-sign-up",
  standalone: true,
  imports: [
    CommonModule,
    HlmButton,
    FormsModule,
    ReactiveFormsModule,
    HlmLabel,
    HlmSpinnerImports,
    BrnInputOtp,
    HlmInputOtp,
    HlmInputOtpGroup,
    HlmInputOtpSlot,
  ],
  host: {
    class: "block",
  },
  templateUrl: "./confirm-sign-up.component.html",
})
export class ConfirmSignUpComponent {
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _router = inject(Router);

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | undefined>(undefined);
  protected readonly hasError = computed(() => Boolean(this.errorMessage()));

  @Output()
  protected readonly confirmSignUpEvent =
    new EventEmitter<AuthenticateStateEnum>();

  protected readonly form = new FormGroup({
    code: new FormControl<string>("", {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
      ],
    }),
  });

  protected readonly transformPaste = (pastedText: string): string =>
    pastedText.replaceAll("-", "");

  constructor() {
    // Clear error message when user starts typing a new code
    this.form.controls.code.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        if (this.hasError()) {
          this.errorMessage.set(undefined);
        }
      });
  }

  protected confirmSignUp(): void {
    this.errorMessage.set(undefined);

    // Mark all fields as touched to show validation errors
    this.form.markAllAsTouched();

    // Check form validity before proceeding
    if (!this.form.valid) {
      this.errorMessage.set("Inserisci il codice OTP a 6 cifre");
      return;
    }

    this.isLoading.set(true);
    const { code } = this.form.getRawValue();
    this._authService
      .confirmSignUp(code)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          // OTP confirmed and auto-login successful, redirect to app
          this.isLoading.set(false);
          void this._router.navigate(["/planning/activities"]);
        },
        error: () => {
          this.isLoading.set(false);
          this.errorMessage.set(
            "Codice OTP non valido, controlla la tua casella di posta",
          );
        },
      });
  }

  protected onResendCodeClick(): void {
    this.isLoading.set(true);
    this._authService
      .resendCode()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (): void => this.isLoading.set(false),
        error: () => this.isLoading.set(false),
      });
  }

  protected onSignInClick(): void {
    this.confirmSignUpEvent.emit(AuthenticateStateEnum.SIGN_IN);
  }
}
